import { readLocalCustomer } from '@/lib/localCustomer';
import { readLastOrderForBusiness } from '@/lib/localLastOrder';
import { normalizeCustomerPhone } from '@/lib/phone';
import { businessOffersDelivery } from '@/lib/assistant/assistantProfileUtils';
import { CHECKOUT_PAYMENT_LABELS } from '@/lib/checkout/paymentMethods';
import { pickCrossSellProducts } from '@/lib/upsell/pickCrossSellProducts';
import {
  hasActiveMesaQrContext,
  readMenuTableContext,
} from '@/lib/menu/menuTableContext';

const BANK_ACCOUNT_TYPE_LABELS = {
  corriente: 'Cuenta Corriente',
  vista: 'Cuenta Vista / RUT',
  ahorro: 'Cuenta de Ahorro',
};

function idKey(x) {
  return x == null || x === '' ? '' : String(x);
}

/** Productos sugeridos del local que aún no están en el carrito. */
export function getLocalSuggestedUpsellProducts(menuData, cartLines, limit = 3) {
  const cap = Math.max(1, Math.min(6, Number(limit) || 3));
  const lines = Array.isArray(cartLines) ? cartLines : [];
  const products = (menuData?.products || []).filter((p) => p && p.availability !== 'hidden');
  const ids = menuData?.business?.suggested_product_ids || [];
  const cartIds = new Set(lines.map((l) => idKey(l.product?.id)).filter(Boolean));
  const picked = [];
  const seen = new Set();

  const pushUnique = (p) => {
    const k = idKey(p?.id);
    if (!p || !k || seen.has(k) || cartIds.has(k)) return;
    seen.add(k);
    picked.push(p);
  };

  const anchor = lines.length ? lines[lines.length - 1]?.product : null;
  if (anchor) {
    for (const p of pickCrossSellProducts(anchor, { menuData, cartLines: lines, limit: cap })) {
      if (picked.length >= cap) break;
      pushUnique(p);
    }
  }

  for (const id of ids) {
    if (picked.length >= cap) break;
    const p = products.find((x) => idKey(x.id) === idKey(id));
    pushUnique(p);
  }

  if (picked.length < cap) {
    const bySales = [...products]
      .filter((p) => !cartIds.has(idKey(p.id)) && !seen.has(idKey(p.id)))
      .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0));
    for (const p of bySales) {
      if (picked.length >= cap) break;
      pushUnique(p);
    }
  }

  return picked.slice(0, cap);
}

export function mergeCheckoutPrefill(sessionMemoryRef, patch) {
  sessionMemoryRef.current.pendingCheckoutPrefill = {
    ...(sessionMemoryRef.current.pendingCheckoutPrefill || {}),
    ...(patch || {}),
  };
  return sessionMemoryRef.current.pendingCheckoutPrefill;
}

export function getCheckoutCustomerSnapshot(businessId, prefill = {}) {
  const c = readLocalCustomer(businessId) || {};
  return {
    name: String(prefill.customerName || c.name || '').trim(),
    phone: normalizeCustomerPhone(prefill.customerPhone || c.phone || ''),
    address: String(prefill.address || c.address || '').trim(),
    email: String(c.email || '').trim(),
  };
}

/** Datos guardados (cliente + último pedido) para ofrecer “mantener información anterior”. */
export function getStoredCheckoutDefaults(businessId, serviceType) {
  const c = readLocalCustomer(businessId) || {};
  const last = readLastOrderForBusiness(businessId);
  const name = String(last?.customerName || c.name || '').trim();
  const phone = normalizeCustomerPhone(last?.customerPhone || c.phone || '');
  if (!name || phone.length < 8) return null;

  const st = serviceType || last?.serviceType || last?.service_type || 'delivery';

  if (st === 'delivery') {
    const addr = String(last?.address || c.address || '').trim();
    if (!addr) return null;
  }

  if (st === 'mesa') {
    const tableNumber = String(
      readMenuTableContext(businessId)?.tableNumber || last?.tableNumber || '',
    ).trim();
    if (!tableNumber) return null;
  }

  return {
    customerName: name,
    customerPhone: phone,
    customerEmail: String(c.email || '').trim(),
    address: st === 'delivery' ? String(last?.address || c.address || '').trim() : '',
    markerPosition:
      last?.markerPosition && typeof last.markerPosition === 'object' ? last.markerPosition : null,
    tableNumber: String(last?.tableNumber || '').trim(),
    paymentMethod: last?.paymentMethod || last?.payment_method || 'efectivo',
    cash_amount: last?.cash_amount,
    tip: last?.tip,
  };
}

export function hasStoredCheckoutDefaults(businessId, serviceType) {
  return getStoredCheckoutDefaults(businessId, serviceType) != null;
}

/** Cliente que ya completó al menos un pedido en este local (localStorage). */
export function hasPreviousOrderForBusiness(businessId) {
  const last = readLastOrderForBusiness(businessId);
  return Boolean(last && Array.isArray(last.items) && last.items.length > 0);
}

/** Pantalla “mantener / cambiar datos” solo si ya pidió antes y hay datos reutilizables. */
export function shouldOfferCheckoutDataPreference(businessId, serviceType) {
  if (!hasPreviousOrderForBusiness(businessId)) return false;
  return hasStoredCheckoutDefaults(businessId, serviceType);
}

export function resolvePreferredServiceTypeFromHistory(businessId, serviceTypes = [], business = null) {
  if (business && hasActiveMesaQrContext(businessId, business)) return 'mesa';
  const last = readLastOrderForBusiness(businessId);
  const stRaw = last?.serviceType || last?.service_type || '';
  if (stRaw === 'delivery' || stRaw === 'retiro' || stRaw === 'mesa') {
    if (serviceTypes.includes(stRaw)) return stRaw;
  }
  if (serviceTypes.includes('delivery')) return 'delivery';
  if (serviceTypes.includes('retiro')) return 'retiro';
  if (serviceTypes.includes('mesa')) return 'mesa';
  return 'retiro';
}

/** Paso inicial al abrir checkout desde el menú (sin bootstrap del asistente). */
export function resolveCheckoutEntryStep(businessId, serviceTypes = [], business = null) {
  if (business && hasActiveMesaQrContext(businessId, business)) {
    if (shouldOfferCheckoutDataPreference(businessId, 'mesa')) return 'data-preference';
    return 'customer';
  }
  if (!hasPreviousOrderForBusiness(businessId)) return 'service';
  const st = resolvePreferredServiceTypeFromHistory(businessId, serviceTypes, business);
  if (shouldOfferCheckoutDataPreference(businessId, st)) return 'data-preference';
  return 'service';
}

/** Paso después de revisar el carrito en checkout rápido. */
export function resolveCheckoutStepAfterCart(businessId, serviceTypes = [], business = null) {
  if (business && hasActiveMesaQrContext(businessId, business)) {
    if (shouldOfferCheckoutDataPreference(businessId, 'mesa')) return 'data-preference';
    return 'customer';
  }
  return 'service';
}

/** @returns {{ nameOrPhone: boolean, address: boolean, payment: boolean, cashAmount: boolean, serviceType: string }} */
export function getMissingCheckoutFields({ business, businessId, prefill = {} }) {
  const snap = getCheckoutCustomerSnapshot(businessId, prefill);
  const payment = prefill.paymentMethod || prefill.payment_method || null;
  const serviceType =
    prefill.serviceType ||
    prefill.service_type ||
    (businessOffersDelivery(business) ? 'delivery' : 'retiro');
  const needsAddress = serviceType === 'delivery' && businessOffersDelivery(business);

  return {
    nameOrPhone: !snap.name || snap.phone.length < 8,
    address: needsAddress && snap.address.length < 5,
    payment: !payment,
    cashAmount:
      (payment === 'efectivo' || payment === 'cash') &&
      !(Number(prefill.cash_amount ?? prefill.cashAmount) > 0),
    serviceType,
  };
}

export function resolveCheckoutBootstrapStep(order, customer, serviceTypes = []) {
  const name = String(order?.customerName || customer?.name || '').trim();
  const phone = normalizeCustomerPhone(order?.customerPhone || customer?.phone || '');
  if (!name || phone.length < 8) return 'customer';

  const stRaw = order?.serviceType || order?.service_type || '';
  const st =
    stRaw === 'delivery' || stRaw === 'retiro' || stRaw === 'mesa'
      ? stRaw
      : serviceTypes.includes('delivery')
        ? 'delivery'
        : serviceTypes.includes('retiro')
          ? 'retiro'
          : 'mesa';

  if (st === 'delivery' && !String(order?.address || customer?.address || '').trim()) {
    return 'address';
  }
  if (st === 'mesa' && !String(order?.tableNumber || '').trim()) {
    return 'mesa';
  }
  if (!order?.paymentMethod && !order?.payment_method) return 'payment';
  return 'review';
}

export function formatTransferBankMessage(business, total) {
  const lines = [
    '🏦 Datos para transferencia',
    '',
    `Banco: ${business?.bank_name || '—'}`,
    `Tipo: ${BANK_ACCOUNT_TYPE_LABELS[business?.bank_account_type] || business?.bank_account_type || '—'}`,
    `Cuenta: ${business?.bank_account_number || '—'}`,
    `RUT: ${business?.bank_rut || '—'}`,
    `Titular: ${business?.bank_holder_name || '—'}`,
  ];
  if (typeof total === 'number' && total > 0) {
    lines.push('', `Monto: $${Math.round(total).toLocaleString('es-CL')}`);
  }
  lines.push('', 'Cuando transfieras, confirmá el pedido y subí el comprobante en el siguiente paso.');
  return lines.join('\n');
}

export function paymentMethodLabel(method) {
  if (method === 'efectivo') return CHECKOUT_PAYMENT_LABELS.efectivo;
  if (method === 'transferencia') return CHECKOUT_PAYMENT_LABELS.transferencia;
  if (method === 'online_manual' || method === 'online_card') {
    return CHECKOUT_PAYMENT_LABELS.online_manual_card;
  }
  return String(method || '');
}
